import { ChevronLeft, ChevronRight } from 'lucide-react'
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isToday,
  isSameMonth,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns'
import { useCallback, useMemo, useState } from 'react'
import { Button } from 'components/Button'
import { Text } from 'components/Text'
import type { CalendarEntry } from '../types'
import { getChannelConfig } from './CalendarEntryCard'

const WEEKDAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

interface MiniCalendarProps {
  entries: CalendarEntry[]
  currentDate: Date
  onDateSelect: (date: Date) => void
}

export function MiniCalendar({ entries, currentDate, onDateSelect }: MiniCalendarProps) {
  const [viewMonth, setViewMonth] = useState(currentDate)

  const days = useMemo(() => {
    const monthStart = startOfMonth(viewMonth)
    const monthEnd = endOfMonth(viewMonth)
    return eachDayOfInterval({
      start: startOfWeek(monthStart),
      end: endOfWeek(monthEnd),
    })
  }, [viewMonth])

  const entriesByDate = useMemo(() => {
    const map = new Map<string, CalendarEntry[]>()
    for (const entry of entries) {
      const existing = map.get(entry.date) ?? []
      existing.push(entry)
      map.set(entry.date, existing)
    }
    return map
  }, [entries])

  const handlePrev = useCallback(() => setViewMonth(m => subMonths(m, 1)), [])
  const handleNext = useCallback(() => setViewMonth(m => addMonths(m, 1)), [])

  const selectedDateStr = format(currentDate, 'yyyy-MM-dd')

  return (
    <div className="w-full select-none">
      <div className="mb-2 flex items-center justify-between">
        <Button variant="t3-ghost" size="t3-sm" onClick={handlePrev} aria-label="Previous month">
          <ChevronLeft size={14} />
        </Button>
        <Text variant="body-sm" weight="semibold" tone="paper-dim">
          {format(viewMonth, 'MMMM yyyy')}
        </Text>
        <Button variant="t3-ghost" size="t3-sm" onClick={handleNext} aria-label="Next month">
          <ChevronRight size={14} />
        </Button>
      </div>

      <div className="mb-1 grid grid-cols-7 gap-0">
        {WEEKDAY_LABELS.map(d => (
          <div key={d} className="py-0.5 text-center text-[9px] font-medium text-paper-faint">
            {d}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-0">
        {days.map(day => {
          const dateStr = format(day, 'yyyy-MM-dd')
          const inMonth = isSameMonth(day, viewMonth)
          const today = isToday(day)
          const isSelected = dateStr === selectedDateStr
          const dayEntries = entriesByDate.get(dateStr) ?? []

          return (
            <button
              key={dateStr}
              onClick={() => onDateSelect(day)}
              className={`relative flex flex-col items-center py-0.5 transition-colors ${
                inMonth ? '' : 'opacity-30'
              }`}
            >
              <span
                className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] ${
                  isSelected
                    ? 'bg-primary font-semibold text-paper'
                    : today
                      ? 'font-semibold text-primary'
                      : 'text-paper-dim hover:bg-bg-3'
                }`}
              >
                {format(day, 'd')}
              </span>
              {/* Content density dots */}
              {dayEntries.length > 0 && (
                <div className="mt-px flex gap-px">
                  {dayEntries.slice(0, 3).map(e => {
                    const cfg = getChannelConfig(e.channel)
                    return (
                      <span
                        key={e.id}
                        className="h-1 w-1 rounded-full"
                        style={{ backgroundColor: cfg.color }}
                      />
                    )
                  })}
                </div>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}
